import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, Settings2, Gavel, HeartHandshake, Briefcase, Layers, LogOut, ShieldCheck, ArrowUpRight } from 'lucide-react';
import { useAuth, API } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { HOME, AUTH } from '@/constants/testIds';

const ICONS = {
  Users, Settings2, Gavel, HeartHandshake, Briefcase, Layers,
};

const TILE_TESTIDS = {
  'hr': HOME.tileHr,
  'operations': HOME.tileOperations,
  'compliance': HOME.tileCompliance,
  'client-services': HOME.tileClientServices,
  'wealth-advisors': HOME.tileWealthAdvisors,
  'alternative-solutions': HOME.tileAlternativeSolutions,
};

export default function LandingPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/categories`, { withCredentials: true });
        setCategories(data);
      } catch (e) {
        setCategories([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div data-testid={HOME.landing} className="min-h-screen bg-[#FAFAFA]">
      {/* Nav */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid={HOME.brandLogo} className="flex items-center gap-3 group">
            <Logo variant="mark" className="h-9 w-9 rounded-md" alt="STPK" />
            <div className="hidden sm:flex items-baseline gap-2">
              <span className="font-display text-xl tracking-wide text-[#0A2540]">STEELPEAK</span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400">STPK RAG</span>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            {user?.role === 'admin' && (
              <Link to="/admin" data-testid={HOME.adminLink}>
                <Button
                  variant="ghost"
                  className="text-slate-600 hover:text-[#0A2540] gap-2"
                >
                  <ShieldCheck className="h-4 w-4" />
                  <span className="hidden sm:inline">Admin</span>
                </Button>
              </Link>
            )}
            <div className="hidden md:flex items-center gap-2 pl-3 border-l border-slate-200">
              <div className="text-right">
                <div className="text-xs font-medium text-[#0A2540]">{user?.name || user?.email}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">{user?.role}</div>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid={AUTH.logoutBtn}
              className="text-slate-500 hover:text-[#0A2540] gap-2"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-12">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#D4AF37]">
            <div className="h-px w-10 bg-[#D4AF37]" />
            <span>Knowledge Network</span>
          </div>
          <h1 className="mt-6 font-display text-5xl md:text-6xl leading-[1.05] text-[#0A2540]">
            Ask the firm.<br />
            <span className="text-slate-400">Get answers with sources.</span>
          </h1>
          <p className="mt-6 text-base text-slate-500 max-w-xl leading-relaxed">
            Select a knowledge domain to open a private, category-scoped chat.
            Every answer is grounded exclusively in STEELPEAK-approved documents,
            cited inline.
          </p>
        </div>
      </section>

      {/* Tiles */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-64 bg-white border border-slate-200 rounded-lg animate-pulse"
              />
            ))}
          {!loading &&
            categories.map((cat) => {
              const Icon = ICONS[cat.icon] || Users;
              return (
                <Link
                  key={cat.id}
                  to={`/chat/${cat.id}`}
                  data-testid={TILE_TESTIDS[cat.id]}
                  className="stpk-tile group block bg-white border border-slate-200 rounded-lg p-10 relative overflow-hidden"
                >
                  <div className="flex items-start justify-between">
                    <div className="h-12 w-12 border border-slate-200 group-hover:border-[#D4AF37] flex items-center justify-center rounded-md transition-colors">
                      <Icon className="h-6 w-6 text-[#0A2540]" strokeWidth={1.5} />
                    </div>
                    <ArrowUpRight className="h-5 w-5 text-slate-300 group-hover:text-[#D4AF37] transition-colors" />
                  </div>

                  <div className="mt-10">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-3">
                      Category
                    </div>
                    <h3 className="font-display text-3xl text-[#0A2540] leading-tight">
                      {cat.name}
                    </h3>
                    <p className="mt-3 text-sm text-slate-500 leading-relaxed max-w-md">
                      {cat.description}
                    </p>
                  </div>

                  <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-xs text-slate-400">
                      {cat.document_count} {cat.document_count === 1 ? 'document' : 'documents'}
                    </span>
                    <span className="text-xs uppercase tracking-[0.2em] text-[#0A2540] group-hover:text-[#D4AF37] transition-colors">
                      Open chat →
                    </span>
                  </div>
                </Link>
              );
            })}
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between text-xs text-slate-400">
          <div>© STEELPEAK — Confidential Knowledge Network</div>
          <div className="uppercase tracking-[0.25em]">Powered by Ollama · Local RAG</div>
        </div>
      </footer>
    </div>
  );
}
